import { useState, useEffect } from "react";
import { X, Plus, Minus, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { usePublicProductAddons, ProductAddon, ProductAddonGroup } from "@/hooks/usePublicProductAddons";
import type { PublicProduct } from "@/hooks/usePublicMenu";

export interface SelectedAddon {
  id: string;
  name: string;
  price: number;
}

interface TabletProductDetailSheetProps {
  product: PublicProduct | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAddToCart: (product: PublicProduct, quantity: number, notes: string, addons: SelectedAddon[]) => void;
}

export function TabletProductDetailSheet({
  product,
  open,
  onOpenChange,
  onAddToCart,
}: TabletProductDetailSheetProps) {
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState("");
  const [notesOpen, setNotesOpen] = useState(false);
  const [selectedAddons, setSelectedAddons] = useState<Map<string, SelectedAddon>>(new Map());

  const { data: addonGroups = [], isLoading: isLoadingAddons } = usePublicProductAddons(product?.id || null);

  // Reset state when product changes
  useEffect(() => {
    if (open && product) {
      setQuantity(1);
      setNotes("");
      setNotesOpen(false);
      setSelectedAddons(new Map());
    }
  }, [open, product?.id]);

  const formatPrice = (price: number) => {
    return `R$ ${price.toFixed(2).replace(".", ",")}`;
  };

  const toggleAddon = (group: ProductAddonGroup, addon: ProductAddon) => {
    setSelectedAddons((prev) => {
      const newMap = new Map(prev);
      const key = addon.id;

      if (newMap.has(key)) {
        newMap.delete(key);
      } else {
        // Check max selections for this group
        const currentGroupSelections = Array.from(newMap.values()).filter(
          (a) => addonGroups.find((g) => g.addons.some((ad) => ad.id === a.id))?.id === group.id
        );

        if (currentGroupSelections.length >= group.maxSelections) {
          const firstInGroup = currentGroupSelections[0];
          if (firstInGroup) {
            newMap.delete(firstInGroup.id);
          }
        }

        newMap.set(key, {
          id: addon.id,
          name: addon.name,
          price: addon.price,
        });
      }

      return newMap;
    });
  };

  const isAddonSelected = (addonId: string) => selectedAddons.has(addonId);

  const getGroupSelectionCount = (group: ProductAddonGroup) => {
    return Array.from(selectedAddons.values()).filter((a) =>
      group.addons.some((ad) => ad.id === a.id)
    ).length;
  };

  const addonsTotal = Array.from(selectedAddons.values()).reduce((sum, a) => sum + a.price, 0);
  const itemTotal = product ? (product.price + addonsTotal) * quantity : 0;

  const canAddToCart = () => {
    for (const group of addonGroups) {
      if (group.isRequired) {
        const count = getGroupSelectionCount(group);
        if (count < group.minSelections) return false;
      }
    }
    return true;
  };

  const handleAddToCart = () => {
    if (!product) return;
    
    onAddToCart(
      product,
      quantity,
      notes,
      Array.from(selectedAddons.values())
    );
    onOpenChange(false);
  };

  if (!product) return null;

  const hasAddons = addonGroups.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] p-0 bg-slate-900 border-slate-800 overflow-hidden flex flex-col">
        {/* Header image - Reduced height */}
        <div className="relative h-40 flex-shrink-0 bg-slate-800">
          {product.image_url ? (
            <img
              src={product.image_url}
              alt={product.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <span className="text-slate-600 text-6xl">🍽️</span>
            </div>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-3 right-3 bg-black/50 hover:bg-black/70 text-white rounded-full"
            onClick={() => onOpenChange(false)}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4 space-y-4">
          {/* Product Info */}
          <div className="space-y-1">
            <h2 className="text-xl font-bold text-white">{product.name}</h2>
            {product.description && (
              <p className="text-sm text-slate-400 line-clamp-2">{product.description}</p>
            )}
          </div>
          
          {/* Price + Notes inline - Always visible */}
          <div className="flex items-start gap-3">
            <p className="text-2xl font-bold text-primary whitespace-nowrap">
              {formatPrice(product.price)}
            </p>
            <div className="flex-1">
              <div className="relative">
                <MessageSquare className="absolute left-3 top-2.5 h-4 w-4 text-slate-500" />
                <input
                  type="text"
                  placeholder="Observação: sem cebola, bem passado..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder:text-slate-500"
                />
              </div>
            </div>
          </div>

          {/* Addon Groups */}
          {hasAddons && !isLoadingAddons && (
            <div className="space-y-4">
              {addonGroups.map((group) => (
                <div key={group.id} className="border border-slate-700 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-white">{group.name}</h3>
                      <p className="text-sm text-slate-400">
                        {group.isRequired
                          ? `Escolha ${group.minSelections === group.maxSelections ? group.minSelections : `de ${group.minSelections} a ${group.maxSelections}`}`
                          : `Selecione até ${group.maxSelections}`}
                      </p>
                    </div>
                    {group.isRequired && (
                      <span className="text-xs bg-red-500/20 text-red-400 px-2 py-1 rounded">
                        Obrigatório
                      </span>
                    )}
                  </div>

                  <div className="space-y-2">
                    {group.addons.map((addon) => (
                      <label
                        key={addon.id}
                        className={`flex items-center justify-between p-4 rounded-lg border cursor-pointer transition-colors ${
                          isAddonSelected(addon.id)
                            ? "border-primary bg-primary/10"
                            : "border-slate-700 hover:bg-slate-800"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Checkbox
                            checked={isAddonSelected(addon.id)}
                            onCheckedChange={() => toggleAddon(group, addon)}
                          />
                          <span className="text-white">{addon.name}</span>
                        </div>
                        <span className="font-medium text-primary">
                          + {formatPrice(addon.price)}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-800 p-6 space-y-4 flex-shrink-0">
          {/* Quantity control */}
          <div className="flex items-center justify-center gap-6">
            <Button
              variant="outline"
              size="icon"
              className="h-12 w-12 border-slate-600"
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
              disabled={quantity <= 1}
            >
              <Minus className="h-5 w-5" />
            </Button>
            <span className="text-3xl font-bold text-white w-16 text-center">{quantity}</span>
            <Button
              variant="outline"
              size="icon"
              className="h-12 w-12 border-slate-600"
              onClick={() => setQuantity(quantity + 1)}
            >
              <Plus className="h-5 w-5" />
            </Button>
          </div>

          {/* Add button */}
          <Button
            className="w-full h-14 text-lg font-semibold"
            onClick={handleAddToCart}
            disabled={!canAddToCart()}
          >
            Adicionar • {formatPrice(itemTotal)}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
